import { Loader } from '@ui-kit/components/loader';
import React, { FormEvent,  useRef, useState } from 'react';
import { ILadderService, StandingsFilter } from '../../model/interfaces';
import styles from './standings.module.scss';
import StandingsRow from './standingsRow/standingsRow';
import classNames from 'classnames';
import { PlayerStatistics, IUserService } from '../../../user/model/interfaces';
import { container } from 'tsyringe';
import { ReactComponent as Search } from '@base/assets/images/search.svg';
import { ReactComponent as Clear } from '@ui-kit/icons/close.svg';
import standingsTableColumns from './standingsTableColumns/standingsTableColumns';
import { t } from '@base/utils/helpers';
import { useAsyncData, useElementSize } from '@utils/hooks';
import MainButton from '@ui-kit/components/mainButton';

export default function Standings(props: { ladderService: ILadderService }) {
  const ref = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);
  const headerRef = useRef(null);
  const userService: IUserService = container.resolve('userService');
  const [filter, setFilter] = useState<StandingsFilter>('rank');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const { data: loadedPlayers, loading, setData } = useAsyncData(
    () => props.ladderService.getStandings(filter),
    filter
  );
  const [players, setPlayers] = useState<PlayerStatistics[]>([]);

  const { height: parentHeight } = useElementSize(ref);
  const { height: headerHeight } = useElementSize(headerRef);
  const bodyHeight = parentHeight - headerHeight;

  async function handleScroll() {
    if (bodyRef.current && !search) {
      const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        if (loadedPlayers) {
          const morePlayers = await props.ladderService.getStandings(filter, loadedPlayers.length + 1);
          setData(loadedPlayers?.concat(morePlayers));
        }
      }
    }
  }

  async function findPlayers(e: FormEvent) {
    e.preventDefault();
    if (!search) return;
    const result = await props.ladderService.findPlayers(search);
    const findPlayers = await Promise.all(result.map(({ userId }, i) => userService.getPlayerStats(userId)));
    setPlayers(findPlayers);
  }

  return (
    <div className={styles.standings} onScroll={handleScroll} ref={ref}>
      <table>
        <thead ref={headerRef}>
          <tr>
            <th className={styles.search}>
              <div>
                {!search ? (
                  <Search
                    onClick={() => {
                      setSearch(search === undefined ? '' : undefined);
                      setPlayers([]);
                    }}
                  />
                ) : (
                  <Clear
                    width={18}
                    height={18}
                    onClick={() => {
                      setSearch(undefined);
                      setPlayers([]);
                    }}
                  />
                )}
                <span>
                  {search !== undefined ? (
                    <form onSubmit={findPlayers}>
                      <input name="name" id="find-player" onChange={(e) => setSearch(e.target.value)} />
                      <MainButton onClick={findPlayers}>{t('search')}</MainButton>
                    </form>
                  ) : (
                    t('player')
                  )}
                </span>
              </div>
            </th>
            {standingsTableColumns.map((header, index) => (
              <th
                key={index}
                onClick={header.filter ? () => setFilter(header.filter!) : undefined}
                className={classNames(
                  header.filter && styles.selectable,
                  header.filter === filter && styles.selected
                )}
              >
                {t(header.title)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ height: `${bodyHeight}px` }} ref={bodyRef}>
          {!loading ? (
            players.length > 0 ? (
              players.map((player) => (
                <StandingsRow
                  key={player.UserID}
                  player={player}
                  ladderService={props.ladderService}
                  userService={userService}
                />
              ))
            ) : (
              loadedPlayers &&
              loadedPlayers.map((player) => (
                <StandingsRow
                  key={player.UserID}
                  player={player}
                  ladderService={props.ladderService}
                  userService={userService}
                />
              ))
            )
          ) : (
            <tr>
              <td>
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
